<template>
  <div class="attach-org-object-sb">
    <div
      class="attach-org-object-sb__stack"
      :class="{ 'attach-org-object-sb__stack--open': showMenu }"
    >
      <button
        type="button"
        class="attach-org-object-sb__trigger"
        :class="{ 'attach-org-object-sb__trigger--disabled': disabled }"
        :disabled="disabled"
        :aria-expanded="showMenu"
        aria-haspopup="menu"
        aria-label="Actions"
        @click.stop="toggleMenu"
      >
        <span class="attach-org-object-sb__trigger-label">Actions</span>
        <span class="attach-org-object-sb__chevron-box" aria-hidden="true">
          <svg
            class="attach-org-object-sb__chevron"
            :class="{ 'attach-org-object-sb__chevron--up': showMenu }"
            width="12"
            height="6"
            viewBox="0 0 12 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 1L6 5L11 1"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
      </button>

      <div v-if="showMenu" class="attach-org-object-sb__menu" role="menu">
        <div class="attach-org-object-sb__menu-items">
          <button
            type="button"
            class="attach-org-object-sb__item"
            role="menuitem"
            @click="select('role')"
          >
            <span class="attach-org-object-sb__item-content">
              <span class="attach-org-object-sb__item-icon">
                <Icon name="org_icon_role" color="dark" size="small" />
              </span>
              <span class="attach-org-object-sb__item-text">Attach Role</span>
            </span>
          </button>
          <button
            type="button"
            class="attach-org-object-sb__item"
            role="menuitem"
            @click="select('responsibility')"
          >
            <span class="attach-org-object-sb__item-content">
              <span class="attach-org-object-sb__item-icon">
                <Icon name="org_icon_responsibility" color="dark" size="small" />
              </span>
              <span class="attach-org-object-sb__item-text">Attach Responsibility</span>
            </span>
          </button>
          <button
            v-if="showImportJobDescription"
            type="button"
            class="attach-org-object-sb__item"
            role="menuitem"
            @click="select('import')"
          >
            <span class="attach-org-object-sb__item-content">
              <span class="attach-org-object-sb__item-icon">
                <img
                  :src="require('@components/assets/upload-cloud-02.svg')"
                  alt=""
                  class="attach-org-object-sb__import-icon"
                />
              </span>
              <span class="attach-org-object-sb__item-text">Import Job Description</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import Icon from "@components/icons/Icon.vue";

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  showImportJobDescription: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["attach-role", "attach-responsibility", "import-job-description"]);

const showMenu = ref(false);

const toggleMenu = () => {
  if (!props.disabled) {
    showMenu.value = !showMenu.value;
  }
};

const closeMenu = () => {
  showMenu.value = false;
};

const select = (kind) => {
  closeMenu();
  if (kind === "responsibility") {
    emit("attach-responsibility");
    return;
  }
  if (kind === "import") {
    emit("import-job-description");
    return;
  }
  emit("attach-role");
};

const handleClickOutside = (event) => {
  const component = event.target.closest(".attach-org-object-sb");
  if (!component) {
    closeMenu();
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
.attach-org-object-sb {
  display: inline-flex;
  width: 228px;
  flex-shrink: 0;
  position: relative;
  overflow: visible;
  z-index: 3;
}

.attach-org-object-sb__stack {
  position: relative;
  width: 228px;
}

.attach-org-object-sb__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 228px;
  height: 40px;
  box-sizing: border-box;
  border: none;
  background-color: #7f56d9;
  color: #ffffff;
  cursor: pointer;
  padding: 8px 10px;
  text-align: left;
  border-radius: 8px;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
}

.attach-org-object-sb__trigger--disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.attach-org-object-sb__trigger-label {
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}

.attach-org-object-sb__chevron-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.attach-org-object-sb__chevron {
  color: #ffffff;
  transition: transform 0.2s ease;
}

.attach-org-object-sb__chevron--up {
  transform: rotate(180deg);
}

.attach-org-object-sb__menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 100;
  width: 228px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #ffffff;
  box-shadow:
    0 4px 6px -2px rgba(16, 24, 40, 0.03),
    0 12px 16px -4px rgba(16, 24, 40, 0.08);
  overflow: visible;
}

.attach-org-object-sb__menu-items {
  display: flex;
  flex-direction: column;
  padding: 4px 0;
}

.attach-org-object-sb__item {
  display: flex;
  width: 100%;
  border: none;
  background: transparent;
  padding: 1px 6px;
  cursor: pointer;
  text-align: left;
}

.attach-org-object-sb__item-content {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 10px 10px 8px;
  border-radius: 6px;
}

.attach-org-object-sb__item:hover .attach-org-object-sb__item-content {
  background-color: #f2edff;
}

.attach-org-object-sb__item-icon {
  display: flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  overflow: clip;
  flex-shrink: 0;
}

.attach-org-object-sb__import-icon {
  width: 20px;
  height: 20px;
  display: block;
}

.attach-org-object-sb__item-text {
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  color: #101828;
  white-space: nowrap;
}
</style>
